import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Calendar, BookOpen, Users, LogOut, Menu, X, Repeat, LayoutDashboard, FileSpreadsheet, Sliders, ShieldAlert, Building2, ClipboardList, ArrowRightLeft, Mail, Database } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const navItems = [
  { name: 'Dashboard', href: '/capacitador/dashboard', icon: LayoutDashboard },
  { name: 'Calendario', href: '/capacitador/calendario', icon: Calendar },
  { name: 'Capacitaciones', href: '/capacitador/cursos', icon: BookOpen },
  { name: 'Colaboradores', href: '/capacitador/empresas', icon: Users },
  { name: 'Encuesta', href: '/capacitador/encuesta', icon: ClipboardList },
  { name: 'Notificaciones', href: '/capacitador/notificaciones', icon: Mail },
  { name: 'Reporte', href: '/capacitador/reporte', icon: FileSpreadsheet },
  { name: 'Bloqueados', href: '/capacitador/bloqueados', icon: ShieldAlert },
  { name: 'Reasignaciones', href: '/capacitador/reasignaciones', icon: ArrowRightLeft },
  { name: 'Admin Inputs', href: '/capacitador/admin-inputs', icon: Database },
  { name: 'Reglas', href: '/capacitador/reglas', icon: Sliders },
];

export const CapacitadorLayout = ({ children }) => {
  const { user, logout, token, hasMultiplePerfiles, switchPerfil } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoImage, setLogoImage] = useState(() => localStorage.getItem('cached_logo_url'));

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await axios.get(`${API}/configuracion`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.logo_image) {
          let imgUrl = res.data.logo_image;
          if (!imgUrl.startsWith('http') && !imgUrl.startsWith('data:'))
            imgUrl = `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
          setLogoImage(imgUrl);
          localStorage.setItem('cached_logo_url', imgUrl);
        }
      } catch {}
    };
    if (!logoImage) fetchLogo();
  }, [token, logoImage]);

  return (
    <div className="min-h-screen bg-slate-50">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-slate-900 to-slate-800 z-40 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo Header */}
        <div className="h-16 border-b border-slate-700/50 flex items-center justify-center px-4">
          {logoImage ? (
            <div className="w-[200px] h-[48px] overflow-hidden flex items-center justify-center">
              <img src={logoImage} alt="Logo" className="max-w-full max-h-full object-contain brightness-0 invert"
                onError={() => { setLogoImage(null); localStorage.removeItem('cached_logo_url'); }}
                data-testid="sidebar-logo" />
            </div>
          ) : (
            <span className="font-bold text-lg text-white tracking-tight">Lomas Bayas</span>
          )}
        </div>

        <nav className="px-3 py-4 space-y-1">
          {navItems.map(item => {
            const active = location.pathname === item.href;
            return (
              <Link key={item.href} to={item.href} onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  active ? 'bg-teal-500/20 text-teal-300 font-medium' : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                }`} data-testid={`nav-${item.name.toLowerCase()}`}>
                <item.icon className="w-4.5 h-4.5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 px-3 py-4 border-t border-slate-700/50">
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-slate-400">{user?.nombre} {user?.apellido}</p>
            <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
          </div>
          {hasMultiplePerfiles && hasMultiplePerfiles() && (
            <button
              onClick={() => { if (switchPerfil()) navigate('/seleccionar-perfil'); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-slate-700 hover:text-white w-full transition-colors mb-1"
              data-testid="cambiar-perfil-btn">
              <Repeat className="w-4.5 h-4.5" />
              Cambiar de perfil
            </button>
          )}
          <button onClick={logout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-red-500/10 hover:text-red-400 w-full transition-colors"
            data-testid="logout-btn">
            <LogOut className="w-4.5 h-4.5" />
            Cerrar Sesion
          </button>
        </div>
      </aside>

      <div className="lg:pl-64 min-h-screen flex flex-col">
        <header className="sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 h-14">
          <button className="lg:hidden p-2 hover:bg-slate-100 rounded-lg" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div />
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-teal-700 bg-teal-50 px-3 py-1 rounded-full">Capacitador</span>
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};
